import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ceederoApi } from "@/api/client";
import { cloudLibraryQueryKey } from "@/cloud/hooks/use-cloud-pull";
import type { VersionPage } from "@/project-history/types/types";
import { projectVersionPath, projectVersionsPath } from "@/project-history/utils/version-links";

const projectVersionsQueryKey = (projectId: string) => ["project-history", projectId] as const;

export const useProjectVersions = (projectId: string) =>
  useInfiniteQuery({
    queryKey: projectVersionsQueryKey(projectId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      ceederoApi.request<VersionPage>({
        path:
          projectVersionsPath(projectId) +
          (pageParam ? `?${new URLSearchParams({ before: pageParam })}` : ""),
        signal,
      }),
    getNextPageParam: (page) => page.next,
  });

/**
 * Removes one version, or every version of the project when no id is given.
 * The bytes it frees stop counting against the account's storage at once, so
 * the library's figures are read again too.
 */
export const useRemoveProjectVersions = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | undefined) =>
      ceederoApi.request<{ removed: number }>({
        method: "DELETE",
        path: id ? projectVersionPath({ projectId, id }) : projectVersionsPath(projectId),
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: projectVersionsQueryKey(projectId) }),
        queryClient.invalidateQueries({ queryKey: cloudLibraryQueryKey }),
      ]),
  });
};
